import React, { useCallback, useMemo, useState } from 'react';

import CircularLoading from '../CircularLoading/CircularLoading';

/**
 * Reusable chapter selector with search, accordion references, and multi-select.
 *
 * Props:
 *  - references: Array of { referenceId, referenceName, chapters: [{ id, name, ... }] }
 *  - selectedChapterIds: Set of selected chapter IDs
 *  - onSelectionChange: (newSet) => void
 *  - loading: boolean (optional)
 *  - error: string (optional)
 */
function ChapterSelector({
  references = [],
  selectedChapterIds = new Set(),
  onSelectionChange,
  loading = false,
  error = '',
}) {
  const [openRefIds, setOpenRefIds] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleReference = useCallback((refId) => {
    setOpenRefIds((prev) => ({
      ...prev,
      [refId]: !prev[refId],
    }));
  }, []);

  const toggleChapter = useCallback(
    (chapterId) => {
      const next = new Set(selectedChapterIds);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      onSelectionChange(next);
    },
    [selectedChapterIds, onSelectionChange],
  );

  const selectAllFromReference = useCallback(
    (chapters) => {
      const next = new Set(selectedChapterIds);
      const allSelected = chapters.every((ch) => next.has(ch.id));

      chapters.forEach((ch) => {
        if (allSelected) {
          next.delete(ch.id);
        } else {
          next.add(ch.id);
        }
      });

      onSelectionChange(next);
    },
    [selectedChapterIds, onSelectionChange],
  );

  // Filter references and chapters by search term
  const filteredReferences = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return references;

    return references
      .map((ref) => {
        const refNameMatch = ref.referenceName.toLowerCase().includes(term);

        // If reference name matches, show all its chapters
        if (refNameMatch) return ref;

        // Otherwise filter chapters by name
        const matchingChapters = (ref.chapters || []).filter((ch) =>
          ch.name.toLowerCase().includes(term),
        );

        if (matchingChapters.length === 0) return null;

        return { ...ref, chapters: matchingChapters };
      })
      .filter(Boolean);
  }, [references, searchTerm]);

  return (
    <div className="chapter-selector">
      <div className="chapter-selector__header">
        <span className="chapter-selector__label">Select Chapters</span>
        {selectedChapterIds.size > 0 && (
          <span className="chapter-selector__count">
            {selectedChapterIds.size} selected
          </span>
        )}
      </div>

      {/* Search input */}
      <div className="chapter-selector__search-wrapper">
        <input
          type="text"
          className="form-control chapter-selector__search"
          placeholder="Search chapters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            type="button"
            className="chapter-selector__search-clear"
            onClick={() => setSearchTerm('')}
            aria-label="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* Chapters list */}
      <div className="chapter-selector__list">
        {loading && (
          <div className="chapter-selector__loading">
            <CircularLoading height={50} width={50} />
          </div>
        )}

        {!loading && filteredReferences.length === 0 && !error && (
          <div className="chapter-selector__empty">
            {searchTerm
              ? 'No chapters match your search.'
              : 'No chapters available.'}
          </div>
        )}

        {!loading && error && (
          <div className="chapter-selector__empty">{error}</div>
        )}

        {!loading &&
          filteredReferences.map((ref) => {
            const isOpen = !!openRefIds[ref.referenceId];
            const chapters = ref.chapters || [];
            const allSelected =
              chapters.length > 0 &&
              chapters.every((ch) => selectedChapterIds.has(ch.id));
            const someSelected = chapters.some((ch) =>
              selectedChapterIds.has(ch.id),
            );

            return (
              <div key={ref.referenceId} className="cs-reference">
                <button
                  type="button"
                  className={`cs-reference__header ${someSelected ? 'cs-reference__header--has-selected' : ''}`}
                  onClick={() => toggleReference(ref.referenceId)}
                >
                  <div className="cs-reference__left">
                    <i
                      className={`cs-reference__arrow ${isOpen ? 'cs-reference__arrow--open' : ''}`}
                    />
                    <span className="cs-reference__name">
                      {ref.referenceName}
                    </span>
                  </div>
                  <div className="cs-reference__right">
                    <span
                      className="cs-reference__select-all"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAllFromReference(chapters);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          selectAllFromReference(chapters);
                        }
                      }}
                    >
                      {allSelected ? 'Deselect all' : 'Select all'}
                    </span>
                    <span className="cs-reference__count">
                      {chapters.length}{' '}
                      {chapters.length === 1 ? 'Chapter' : 'Chapters'}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="cs-reference__body">
                    {chapters.map((ch) => {
                      const isSelected = selectedChapterIds.has(ch.id);

                      return (
                        <div
                          key={ch.id}
                          className={`cs-chapter ${isSelected ? 'cs-chapter--selected' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleChapter(ch.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleChapter(ch.id);
                            }
                          }}
                        >
                          <span className="cs-chapter__name">{ch.name}</span>
                          {ch.questionCount >= 0 && (
                            <span className="cs-chapter__question-count">
                              {ch.questionCount} questions
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default ChapterSelector;
